/**
 * Bait balls: the big decorative shoals, the ones that swirl.
 *
 * These are not the `swarmer` enemies. Those are a handful of animals with real
 * AI that can bite you. A bait ball is several hundred fish that exist to be
 * looked at, and the two want completely different machinery.
 *
 * The obvious way to build one is boids — separation, cohesion, a vortex to
 * orbit — which is what you would do in a DCC tool where the simulation is
 * baked once and never has to run again. In a game it is the wrong shape: the
 * separation rule is O(n²) in the number of fish, and n is the whole point.
 *
 * So the motion is closed form instead. Every fish is a point on a torus that
 * is turning, breathing and drifting, and its position is a pure function of
 * (index, time). Nothing is integrated, nothing is stored, no fish ever asks
 * another fish where it is — the fish never collide because their orbits are
 * laid out so they cannot, not because anything pushes them apart. It costs a
 * few trig calls each and it cannot drift, explode or clump.
 *
 * The one thing that is not closed form is the sub: fish near it are pushed
 * outward, so driving through a bait ball opens a hole in it that closes again
 * behind you. That is the shot everyone remembers, and it is worth the one
 * distance test per fish.
 */

import { BAIT, FISH_FLEX, FISH_MIX } from './config';
import { LAYERS, laneCeiling } from './mesher';
import { clamp, hyp } from '../core/math';

export interface BaitFish {
  x: number;
  y: number;
  z: number;
  /** Heading, radians. Derived from the orbit tangent, never integrated. */
  aim: number;
  facing: number;
  /** Roll, from how hard the orbit is turning. */
  bank: number;
  /**
   * Body curvature — how far the tail is displaced from the nose's heading,
   * in model units. A fish going round a bend is bent; rotating a rigid rod to
   * point along the arc is the thing that makes cheap shoals look like debris.
   */
  curve: number;
  /**
   * hl² · size · gain, precomputed. Curvature of a circular path is 1/r, and a
   * body of half-length hl riding it has its tail off the tangent by about
   * hl²/2r — so the per-frame cost of the whole effect is one divide.
   */
  bendScale: number;
  size: number;
  species: number;
  /** Per-fish animation phase, so tails do not beat in unison. */
  phase: number;
  /**
   * Per-fish tail-beat rate. Scattering the phase alone was not enough: several
   * hundred fish beating at one frequency interfere, and the ball shimmers
   * instead of swimming.
   */
  rate: number;
}

/**
 * How a shoal moves. A world where every shoal is a tornado is as monotonous as
 * one where none of them are.
 */
export type ShoalMode =
  /** A turning ball. Inner orbits run faster, so it swirls. */
  | 'vortex'
  /** Travels in a line, slows, turns around as one, and comes back. */
  | 'cruise';

export interface BaitSpec {
  id: string;
  x: number;
  y: number;
  mode: ShoalMode;
  /** 0..1. Bioluminescence, for the shoals that live where light does not. */
  glow: number;
  /** Cruise only: how far it runs before turning around. */
  range: number;
  /** Radius of the ball at its widest. */
  radius: number;
  /** Vertical extent. */
  height: number;
  count: number;
  /** Revolutions per second at the core. */
  spin: number;
  /**
   * Brightness of this ball's silver, 0..1. Bait balls are not coloured: what
   * makes one beautiful is the flash of a thousand identical flanks catching
   * the light at once, and a hue fights that. Balls differ in brightness only.
   */
  sheen: number;
}

/** Cheap stable hash → 0..1. Deterministic per fish index. */
function h01(i: number, salt: number): number {
  const s = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return s - Math.floor(s);
}

export class BaitBall {
  readonly fish: BaitFish[] = [];
  /** True while the ball is close enough to be worth simulating. */
  live = false;

  constructor(readonly spec: BaitSpec) {
    for (let i = 0; i < spec.count; i++) {
      // Down deep the shoal is mostly lantern fish — they are the ones with
      // light organs, and they are the reason anything is visible at all.
      const sp = spec.glow > 0.5 && h01(i, 41) < 0.72
        ? 1
        : FISH_MIX[Math.floor(h01(i, 7) * FISH_MIX.length) % FISH_MIX.length];
      // Mostly a uniform small mass — that is what a bait ball is — but one
      // fish in eight is a good deal larger. Without them the ball reads as a
      // flat texture; with them the eye gets a scale reference and the whole
      // thing gains depth.
      const size = h01(i, 5) < 0.12
        ? 0.62 + h01(i, 3) * 0.38
        : 0.24 + h01(i, 3) * 0.16;
      this.fish.push({
        x: spec.x, y: spec.y, z: 0,
        aim: 0, facing: 1, bank: 0, curve: 0,
        // 1.25 is a deliberate exaggeration of the true hl²/2r. The physical
        // amount is correct and almost invisible at this scale; animation reads
        // at arm's length, not on a protractor.
        bendScale: FISH_FLEX[sp][0] * FISH_FLEX[sp][0] * size * 1.25,
        size,
        species: sp,
        phase: h01(i, 11) * Math.PI * 2,
        // Beat rate falls with body length, which is true of real fish and is
        // what stops the big ones looking like they are panicking. The scatter
        // on top is what actually kills the shimmer: identical frequencies beat
        // against each other no matter how the phases are spread.
        rate: (2.05 / Math.sqrt(size / 0.32)) * (0.86 + h01(i, 13) * 0.28),
      });
    }
  }

  /**
   * `t` is the shared world clock. `px`/`py` are the sub, which parts the ball.
   * Returns false when the ball is too far away to be worth drawing.
   */
  update(t: number, px: number, py: number, cullX: number): boolean {
    const s = this.spec;
    this.live = Math.abs(s.x - cullX) < BAIT.cullRange;
    if (!this.live) return false;

    // The whole shoal breathes and wanders, so it never reads as a fixed prop.
    const breathe = 0.82 + Math.sin(t * 0.19 + s.x * 0.03) * 0.18;
    const cruising = s.mode === 'cruise';

    // A cruising shoal runs one way, slows, turns over as a body, and comes
    // back. Position is a sine so the ends are naturally unhurried, and the
    // heading is its derivative, which means the whole shoal reverses together
    // instead of each fish deciding for itself.
    const runPhase = t * s.spin * 2.4 + s.x * 0.017;
    const cx = cruising
      ? s.x + Math.sin(runPhase) * s.range
      : s.x + Math.sin(t * 0.11 + s.x * 0.01) * s.radius * 0.35;
    // AND NOT OUT OF THE SEA.
    //
    // The enemies have been clamped to a waterline since they were caught
    // climbing out of it; bait balls never were, because their height comes
    // from a spec rather than from steering and nobody expected a spec to put
    // one in the air. A shoal seeded near the surface does exactly that, and
    // since these fish are POSITIONED rather than integrated — every one of
    // them is a closed-form function of `t` — the ones above the line cannot
    // swim back down. They hang there wobbling on the spot, which is the
    // "initialized but not quite live" fish, seen from a tilted camera.
    //
    // Clamped once, on the ball's centre, rather than per fish: pushing the
    // individuals down would flatten the ball against the ceiling and cost a
    // clamp on every fish every frame. Bait balls ride in the play lane, so
    // that is the lane whose waterline they answer to.
    const lid = laneCeiling(s.x, LAYERS[0]!) - s.height * 0.5 - s.radius * 0.12;
    const cy = Math.min(
      s.y + Math.sin(t * 0.083 + s.x * 0.02) * s.height * 0.3, lid,
    );
    const runDir = Math.cos(runPhase);

    const n = this.fish.length;
    for (let i = 0; i < n; i++) {
      const f = this.fish[i];

      // Where this fish sits in the ball: a height band, and a radius that is
      // widest at the equator so the silhouette is a ball rather than a tube.
      const bandPhase = t * 0.13 * (0.6 + h01(i, 17)) + h01(i, 23) * 6.283;
      const band = Math.sin(bandPhase);
      const rad = s.radius * breathe * (0.28 + 0.72 * Math.sqrt(Math.max(0, 1 - band * band)))
        * (0.55 + h01(i, 29) * 0.45);

      // Inner fish turn faster, which is what makes it swirl rather than spin.
      const w = (s.spin * 6.283) / (0.45 + rad / s.radius);
      const th = t * w + h01(i, 31) * 6.283;

      const ct = Math.cos(th), st = Math.sin(th);
      let x: number, y: number, z: number;
      if (cruising) {
        // A loose lozenge held around the travelling centre, stretched along
        // the direction of travel. The orbit terms survive as a slow shuffle
        // inside the formation, so it is a shoal rather than a rigid lattice.
        x = cx + ct * rad * 1.5;
        y = cy + band * s.height * 0.5 + st * rad * 0.12;
        z = Math.sin(th * 0.5 + h01(i, 37) * 6.283) * rad * BAIT.depthScale;
      } else {
        x = cx + ct * rad;
        y = cy + band * s.height * 0.5;
        z = st * rad * BAIT.depthScale;
      }

      // The sub parts the ball. Purely positional — no forces, no state.
      const dx = x - px, dy = y - py;
      const d = hyp(dx, dy);
      if (d < BAIT.partRadius) {
        const push = (BAIT.partRadius - d) / BAIT.partRadius;
        const inv = 1 / (d || 1);
        x += dx * inv * push * BAIT.partRadius * 0.85;
        y += dy * inv * push * BAIT.partRadius * 0.85;
      }

      // Heading is the tangent of the path, so a fish always points along the
      // path it is actually on. No damping needed: the path is smooth already.
      if (cruising) {
        const tany = Math.cos(bandPhase) * 0.1 * s.height * 0.5;
        f.aim = Math.atan2(tany, runDir);
        f.facing = runDir < 0 ? -1 : 1;
        // Banks over as the shoal turns around, and rides flat down the run.
        f.bank = clamp(-Math.sin(runPhase) * 0.5, -0.5, 0.5);
        // And bends into that turn. A cruising shoal is straight down the run
        // and curled at the two ends, which is where the reversal reads from.
        f.curve = clamp(f.bendScale * -0.11 * Math.sin(runPhase), -0.4, 0.4);
      } else {
        const tanx = -st * rad * w;
        const tany = Math.cos(bandPhase) * 0.13 * s.height * 0.5;
        f.aim = Math.atan2(tany, tanx);
        f.facing = ct * w < 0 ? -1 : 1;
        // A circular orbit holds a steady bank; tighter orbits bank harder.
        f.bank = clamp((s.radius / Math.max(rad, 1)) * 0.22, 0, 0.6) * -Math.sign(w);
        // Bent along the arc, by the curvature of the arc: 1/r. The inner fish
        // are on the tightest orbits and turning fastest, so they curl hardest
        // — which is exactly the shape a real bait ball takes, and it comes out
        // of the motion for free rather than being animated on top of it.
        f.curve = clamp(f.bendScale / Math.max(rad, 2), 0, 0.4) * -Math.sign(w);
      }

      f.x = x;
      f.y = y;
      f.z = z;
    }
    return true;
  }
}
